import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../../../refershTokenManage/axiosConfig";
import { toast } from "sonner";
import { FaTiktok, FaUpload, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { LinearProgress } from "@mui/material";

const TikTokVideoUploader = () => {
    const { tiktokId } = useParams();
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [caption, setCaption] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [accountInfo, setAccountInfo] = useState(null);
    const [isTokenValid, setIsTokenValid] = useState(true);

    useEffect(() => {
        const fetchAccountInfo = async () => {
            console.log(tiktokId);
            const token = localStorage.getItem("token");
            setIsUploading(true);
            try {
                const response = await axiosInstance.post(
                    '/api/tiktok/get-tiktok',
                    {
                        tiktok_id: tiktokId,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.data) {
                    console.log('data', response.data.data);
                    setAccountInfo(response.data.data);
                    setIsTokenValid(!response.data.data.expire_flag);
                    if (response.data.message) {
                        toast.success(response.data.message);
                    }
                } else {
                    throw new Error("No data received from server");
                }
            } catch (error) {
                console.error("Error fetching TikTok account:", error);
                const errorMessage = error.response?.data?.message
                    || error.message
                    || "Failed to load TikTok account information";

                toast.error(errorMessage);

                if (error.response?.status !== 200) {
                    navigate('/tiktok-accounts');
                }
            } finally {
                setIsUploading(false);
            }
        };
        fetchAccountInfo();
    }, [tiktokId, navigate]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (!selectedFile.type.includes('video/')) {
            toast.error('Please select a video file (MP4 or MOV)');
            return;
        }

        if (selectedFile.size > 50 * 1024 * 1024) {
            toast.error('Video file must be less than 50MB');
            return;
        }

        setFile(selectedFile);
        setPreviewUrl(URL.createObjectURL(selectedFile));
    };

    const handleSubmit = async () => {
        if (!file || !isTokenValid) return;

        const token = localStorage.getItem("token");
        setIsUploading(true);
        setProgress(0);

        const formData = new FormData();
        formData.append('video', file);
        formData.append('caption', caption);
        formData.append('tiktok_id', tiktokId);

        try {
            const response = await axiosInstance.post('/api/tiktok/post/video', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setProgress(percentCompleted);
                },
            });

            toast.success(response.data.message);
            navigate('/tiktok-accounts', {
                state: { toastMessage: 'Video posted to TikTok successfully!' },
            });
        } catch (error) {
            toast.error(error.response?.data?.error || 'Upload failed');
        } finally {
            setIsUploading(false);
        }
    };

    if (!accountInfo) {
        return (
            <div className="upload-container">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!isTokenValid) {
        return (
            <div className="upload-container">
                <div className="upload-error">
                    <h3><FaExclamationTriangle /> TikTok Token Expired</h3>
                    <p>This TikTok account's access token has expired or is about to expire.</p>
                    <p>Please reconnect your TikTok account to enable video uploads.</p>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/tiktok-accounts')}
                    >
                        Back to Accounts
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="upload-container">
            <h2><FaTiktok /> Upload Video to @{accountInfo.tiktok}</h2>

            <div className="upload-section">
                {!file ? (
                    <div className="upload-prompt">
                        <input
                            type="file"
                            id="video-upload"
                            accept="video/mp4,video/quicktime"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="video-upload" className="upload-btn">
                            <FaUpload /> Select Video File
                        </label>
                        <p className="small">Max file size: 50MB (MP4 or MOV)</p>
                    </div>
                ) : (
                    <div className="video-preview">
                        <div className="preview-header">
                            <h4>Selected Video</h4>
                            <button onClick={() => setFile(null)}>
                                <FaTimes />
                            </button>
                        </div>
                        <video controls src={previewUrl} />
                        <div className="video-details">
                            <textarea
                                placeholder="Add a caption..."
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                maxLength={150}
                            />
                            <small>{caption.length}/150 characters</small>
                        </div>
                    </div>
                )}
            </div>

            {isUploading && (
                <div className="upload-progress">
                    <LinearProgress variant="determinate" value={progress} />
                    <span>{progress}%</span>
                </div>
            )}

            <div className="upload-actions">
                <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/tiktok-accounts')}
                    disabled={isUploading}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={!file || isUploading}
                >
                    {isUploading ? 'Uploading...' : 'Upload to TikTok'}
                </button>
            </div>
        </div>
    );
};

export default TikTokVideoUploader;